import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';

import { titleTemplate } from '../../hooks/utils';
import { useVideoPlaylist } from '../../hooks/videos';
import { useVideoPlaylistStore, useVideoPlaylistOptions } from '../../store';
import CurrentVideo from '../CurrentVideo';
import MatchdayFilter from '../Filters/MatchdayFilter';
import TeamFilter from '../Filters/TeamFilter';
import VideoList from '../VideoList';

import type { EditorialContent } from '@cobalt/utilities';
import { getIdFromPath, slugSeparator } from '@cobalt/utilities';
import { getVideoUrl } from '@services/LinkRuleService';
import { useTranslateFn } from '@shared/Translate';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { IS_DEBUG } from '@utilities/browser';
import { loadFromAPIs } from '@utilities/fetch';
import { Warn } from '@utilities/logging';

import * as classes from './styles.module.scss';

const originalDocTitle = document.title;

const Main = () => {
  const t = useTranslateFn();
  const { options } = useVideoPlaylistOptions();
  const { videos } = useVideoPlaylist();
  const selectedVideo = useVideoPlaylistStore((state) => state.selectedVideo);
  const setSelected = useVideoPlaylistStore((state) => state.setSelected);
  const previousVideo = useRef(selectedVideo);
  const [pageTitle, setPageTitle] = useState(originalDocTitle);

  const updateSelectedItem = useCallback(
    async (newItem: EditorialContent, replace?: boolean) => {
      setSelected(newItem);

      if (!newItem) return;
      const newTitle = titleTemplate(newItem?.title, previousVideo.current?.title, originalDocTitle);
      setPageTitle(newTitle);
      previousVideo.current = newItem;

      if (options.useRouting && !IS_DEBUG) {
        try {
          let path = await loadFromAPIs<string>(getVideoUrl(newItem.id, 'url/'));
          path = location.search ? `${path}${location.search}` : path;
          replace ? history.replaceState({ path }, '', path) : history.pushState({ path }, '', path);
        } catch (e) {
          Warn(e);
        }
      }

      const videoID = GoogleTagManagerHelper.getFromDataLayer('videoID');
      if (videoID !== newItem.id) {
        GoogleTagManagerHelper.addPageMeta({
          videoID: newItem.id,
          videoTitle: newItem.title,
          videoLength: newItem.duration,
          contentScoring: newItem.contentScoring,
        });
      }
    },
    [setSelected, options.useRouting],
  );

  useEffect(() => {
    updateSelectedItem(selectedVideo);
  }, [selectedVideo, updateSelectedItem]);

  useEffect(() => {
    const updateSelectedItemFromUrl = ({ state }: PopStateEvent) => {
      const idFromPath = getIdFromPath(state.path);
      const newVideo = state.path && videos.find((v) => v.id === idFromPath);
      if (!newVideo) return;
      updateSelectedItem(newVideo);
    };

    window.addEventListener('popstate', updateSelectedItemFromUrl);
    return () => {
      window.removeEventListener('popstate', updateSelectedItemFromUrl);
    };
  }, [videos, updateSelectedItem]);

  useEffect(() => {
    if (selectedVideo) return;

    const newSelectedVideo =
      videos?.find((v) => v.id === options.selectedVideoId?.split(slugSeparator)?.at(0)) ?? videos?.at(0) ?? null;

    updateSelectedItem(newSelectedVideo, true);
  }, [options.selectedVideoId, videos, updateSelectedItem, selectedVideo]);

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      {(options.teamFilter || options.matchdayFilter) && (
        <div className={classes.filters}>
          <span className={classes.filtersLabel}>{t('filter_by')}</span>
          {options.teamFilter && <TeamFilter />}
          {options.matchdayFilter && <MatchdayFilter />}
        </div>
      )}
      <div className={classes.container}>
        <CurrentVideo />
        <VideoList />
      </div>
    </>
  );
};

export default memo(Main);
